/**
 * ComposeTab — Personalized invite message composer with envelope image generation.
 *
 * Flow:
 *  1. Edit/confirm template (stored in guest_site_config.invite_message_template)
 *  2. Select one or more guests/parties via checkboxes
 *  3. Click "Generate" to produce personalized content for each selection
 *  4. Each result card shows: envelope preview, full message, copy/download actions
 *  5. Optionally mark a Journey step as complete
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Copy, Check, Search, ChevronDown, ChevronUp, Download, Image as ImageIcon, AlertTriangle } from 'lucide-react';
import EnvelopeCanvas from './EnvelopeCanvas';
import { resolveGuestGreeting } from '../../../lib/resolveGuestGreeting';

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_SITE = process.env.REACT_APP_PUBLIC_SITE_URL || window.location.origin;

const GOLD    = '#C6A75A';
const DARK    = '#1B1812';
const SURFACE = '#FFFCF7';
const BORDER  = 'rgba(198,167,90,0.22)';
const MUTED   = '#7A6A40';

const DEFAULT_TEMPLATE = `Hi {{name}},

You are cordially invited to celebrate Carol's 80th Birthday — 80 Years of Connection.

Saturday, July 25th, 2026
Season's Catering, Washington Township, NJ

Your personal invitation link:
{{rsvp_link}}

Please kindly reply by June 15th, 2026.
We look forward to celebrating with you.

With love,
The Family`;

function buildInviteLink(token) {
  if (!token) return null;
  return `${PUBLIC_SITE}/invite/${token}`;
}

function fillTemplate(template, name, link) {
  return template
    .replace(/\{\{name\}\}/gi,      name || '(Guest)')
    .replace(/\{\{rsvp_link\}\}/gi, link || '(link not available — assign RSVP token first)');
}

// ─── Section card wrapper ──────────────────────────────────────────────────────
const Card = ({ title, children, collapsible = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 12,
      background: SURFACE, marginBottom: 16, overflow: 'hidden' }}>
      <div
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
        style={{ padding: '12px 16px', background: 'rgba(198,167,90,0.06)',
          borderBottom: open ? `1px solid ${BORDER}` : 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: collapsible ? 'pointer' : 'default' }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '1px', color: MUTED }}>{title}</span>
        {collapsible && (open
          ? <ChevronUp size={14} color={MUTED} />
          : <ChevronDown size={14} color={MUTED} />)}
      </div>
      {open && <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
};

const Btn = ({ onClick, disabled, children, variant = 'primary', style: s = {} }) => {
  const base = {
    padding: '9px 18px', borderRadius: 8, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 700, fontSize: 12, letterSpacing: '0.5px', opacity: disabled ? 0.5 : 1,
    transition: 'all 0.18s ease', display: 'inline-flex', alignItems: 'center', gap: 6, ...s,
  };
  const vars = {
    primary: { background: `linear-gradient(135deg,${GOLD},#B89040)`, color: '#1B1812',
      boxShadow: '0 2px 8px rgba(198,167,90,0.25)' },
    ghost: { background: 'transparent', color: MUTED, border: `1px solid ${BORDER}` },
    danger: { background: 'rgba(180,60,60,0.10)', color: '#8B2020', border: '1px solid rgba(180,60,60,0.25)' },
  };
  return <button onClick={disabled ? undefined : onClick} style={{ ...base, ...vars[variant] }}>{children}</button>;
};

// ─── Per-guest result card ─────────────────────────────────────────────────────
function GeneratedCard({ target, template }) {
  const [envelopeDataUrl, setEnvelopeDataUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const link = buildInviteLink(target.token);
  // Greeting surfaces (message body {{name}}, envelope big script) use
  // greetingName, NOT the picker title. For solos this is the first name only
  // ("Evelyn"); for multi it equals the picker title (e.g. "Carol & Lito").
  // Picker title (target.name) keeps full names so admin can disambiguate.
  const greetingName = target.greetingName || target.name;
  const message = fillTemplate(template, greetingName, link || '(link not available — assign RSVP token first)');
  const firstName = target.firstName || greetingName.split(' ')[0];
  const hasToken = !!target.token;

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
    } catch {
      const el = document.createElement('textarea');
      el.value = message;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const downloadImage = () => {
    if (!envelopeDataUrl) return;
    const a = document.createElement('a');
    a.href = envelopeDataUrl;
    a.download = `invite-${firstName.toLowerCase()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 12, background: SURFACE,
      marginBottom: 16, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '10px 16px', background: 'rgba(198,167,90,0.06)',
        borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          background: hasToken ? GOLD : 'rgba(180,60,60,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700, color: hasToken ? '#1B1812' : '#8B2020' }}>
          {firstName.charAt(0).toUpperCase()}
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: DARK }}>{target.name}</span>
        {!hasToken && (
          <span style={{ fontSize: 10, color: '#8B2020', display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
            <AlertTriangle size={10} /> No token
          </span>
        )}
      </div>

      <div style={{ padding: 16 }}>
        {/* Envelope preview */}
        {hasToken && (
          <div style={{ marginBottom: 14, borderRadius: 8, overflow: 'hidden',
            border: `1px solid ${BORDER}`, background: '#F0EDE4' }}>
            <div data-testid={`envelope-preview-${target.key}`} style={{ position: 'relative' }}>
              <EnvelopeCanvas
                guestFirstName={firstName}
                onImageReady={(url) => setEnvelopeDataUrl(url)}
              />
              {envelopeDataUrl && (
                <img src={envelopeDataUrl} alt={`Invite for ${firstName}`}
                  style={{ width: '100%', display: 'block', borderRadius: 8 }} />
              )}
            </div>
          </div>
        )}

        {/* Message preview */}
        <div style={{ background: '#F8F5EE', border: `1px solid ${BORDER}`,
          borderRadius: 10, padding: '14px 16px', marginBottom: 12,
          fontFamily: 'Georgia, serif', fontSize: 13, color: DARK,
          lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          data-testid={`message-preview-${target.key}`}>
          {message}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Btn onClick={copyMessage} data-testid={`copy-msg-${target.key}`}>
            {copied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy Message</>}
          </Btn>
          {envelopeDataUrl && (
            <Btn variant="ghost" onClick={downloadImage} data-testid={`download-img-${target.key}`}>
              <Download size={13} /> Download Image
            </Btn>
          )}
          {hasToken && link && (
            <a href={link} target="_blank" rel="noreferrer"
              style={{ fontSize: 11, color: MUTED, textDecoration: 'underline', textUnderlineOffset: 2, marginLeft: 4 }}>
              Test link
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function ComposeTab({ guests = [] }) {
  const [template, setTemplate]         = useState(DEFAULT_TEMPLATE);
  const [templateSaved, setTemplateSaved] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [search, setSearch]             = useState('');
  const [parties, setParties]           = useState([]);
  const [partiesLoaded, setPartiesLoaded] = useState(false);
  // Selection stored as a Set of guest IDs (stable source of truth).
  // Target keys are derived and can drift when the `guests` prop refreshes mid-session
  // and a guest's party_id flips from falsy → truthy — which previously caused
  // selectedKeys to no longer match any target, producing an empty Generated section.
  const [selectedGuestIds, setSelectedGuestIds] = useState(new Set());
  const [generated, setGenerated]       = useState(false);

  const [journeySteps, setJourneySteps] = useState([]);
  const [selectedStepId, setSelectedStepId] = useState('');
  const [markingDone, setMarkingDone]   = useState(false);
  const [markedIds, setMarkedIds]       = useState(new Set());
  // Per-member journey selection. When a target is generated, all member
  // guest_ids start checked; admin can deselect any individual before pressing
  // "Mark as …". Mark fires only for checked members. (Feb 2026 — group support.)
  const [journeyMemberIds, setJourneyMemberIds] = useState(new Set());

  // ─── Picker sort + "Not yet sent" filter (Feb 2026) ──────────────────────
  // Defaults preserve current order/visibility. Sort options match the
  // Guest tab vocabulary; filter source-of-truth is the Official Invitation
  // journey step completions (NOT email-send history, NOT invitation_status).
  const [sortMode, setSortMode]                 = useState('default');
  const [filterUnsent, setFilterUnsent]         = useState(false);
  const [officialStepId, setOfficialStepId]     = useState(null);
  const [officialCompletions, setOfficialCompletions] = useState(new Set());

  // ─── Layout mode (Feb 2026 — 3-column desktop reflow) ───────────────────
  // Pure presentational reflow:
  //   ≥1280px → 3 cols (Template | Picker+Preview | Journey)
  //   1024–1279px → 2 cols (Template | Picker+Preview), Journey full-width below
  //   <1024px → single column (byte-identical to pre-reflow stacked layout)
  const getLayoutMode = () => {
    if (typeof window === 'undefined') return 'narrow';
    const w = window.innerWidth;
    if (w >= 1280) return 'wide';
    if (w >= 1024) return 'mid';
    return 'narrow';
  };
  const [layoutMode, setLayoutMode] = useState(getLayoutMode);
  useEffect(() => {
    const onResize = () => setLayoutMode(getLayoutMode());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isWide = layoutMode === 'wide';
  const isMid  = layoutMode === 'mid';

  // Refetch parties whenever the guests prop changes (i.e. parent refreshed
  // after a delete / ungroup / regroup). Without this, the Compose Invite
  // target list keeps a stale snapshot of parties from mount and shows
  // groupings or guests that no longer exist. Ruth/Manny ungroup-bug + deleted
  // guest still appearing — Feb 2026, scope: Compose-only Fix 1.
  const refetchParties = useCallback(() => {
    fetch(`${API}/api/parties`)
      .then(r => r.json())
      .then(d => { setParties(Array.isArray(d) ? d : (d.parties || [])); })
      .catch(() => {})
      .finally(() => { setPartiesLoaded(true); });
  }, []);

  useEffect(() => {
    fetch(`${API}/api/guest-site-config`)
      .then(r => r.json())
      .then(d => { if (d?.invite_message_template) setTemplate(d.invite_message_template); })
      .catch(() => {});
    fetch(`${API}/api/journey/steps`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setJourneySteps(d); })
      .catch(() => {});
    refetchParties();
  }, [refetchParties]);

  // Re-pull parties whenever the `guests` prop reference changes (parent's
  // refreshAll() resets that array, e.g. after a deletion or grouping change).
  // We deliberately key on `guests` itself (not length) so any meaningful
  // refresh upstream triggers a parties resync — keeping the picker honest.
  useEffect(() => {
    if (!partiesLoaded) return; // skip the very first mount; the effect above already loaded
    refetchParties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guests]);

  // ─── Resolve Official Invitation step + fetch its completions ────────────
  // Source of truth for the "Not yet sent (Official Invitation)" filter.
  // Same matcher used in markJourneyStep: step_code first (survives renames),
  // exact name match second. Does NOT match "Official Invitation Confirmation"
  // or any other step that contains those words.
  const fetchOfficialCompletions = useCallback(async (stepId) => {
    if (!stepId) return;
    try {
      const r = await fetch(`${API}/api/journey/steps/${stepId}/completions`);
      const d = await r.json();
      if (Array.isArray(d)) {
        setOfficialCompletions(new Set(d.map(c => c.guest_id).filter(Boolean)));
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!Array.isArray(journeySteps) || journeySteps.length === 0) return;
    const step = journeySteps.find(s => {
      const code = (s.step_code || '').trim().toLowerCase();
      const name = (s.name || s.step_name || '').trim().toLowerCase();
      return code === 'official_invitation' || name === 'official invitation';
    });
    if (!step) return;
    const sid = step.id || step._id;
    setOfficialStepId(sid);
    fetchOfficialCompletions(sid);
  }, [journeySteps, fetchOfficialCompletions]);

  const saveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await fetch(`${API}/api/guest-site-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invite_message_template: template }),
      });
      setTemplateSaved(true);
      setTimeout(() => setTemplateSaved(false), 2000);
    } catch {}
    setSavingTemplate(false);
  };

  // Build target list
  const targets = useCallback(() => {
    const results = [];
    const guestsWithParty = new Set();

    parties.forEach(p => {
      const members = guests.filter(g => g.party_id === p._id || g.party_id === p.id);
      if (members.length === 0) return;
      members.forEach(g => guestsWithParty.add(g.id));
      const token = p.rsvp_token || p.party_rsvp_token || '';
      const isSolo = members.length === 1;
      const primary = members.find(m => m.is_primary === true) ?? members[0];

      // Display-name precedence (Feb 2026 — aligned across every surface
      // via shared resolveGuestGreeting() in lib/resolveGuestGreeting.js):
      //   - Solo party  → admin picker shows "<first> <last>" (target.name);
      //                   guest-facing surfaces show "<first>" only
      //                   (target.greetingName).
      //   - Multi-party → call resolveGuestGreeting() so picker, generated
      //                   message, envelope, and public RSVP greeting all
      //                   produce the SAME string for the SAME target.
      // The resolver internally handles: admin group_name → admin party_name
      // (skipping auto-stamps) → "<lastname> Family" → "<first> and Guests".
      const partyNameTrim = (p.party_name || '').trim();
      let displayName;
      let greetingName;
      if (isSolo) {
        const first = members[0].first_name || '';
        const last  = members[0].last_name || '';
        displayName  = `${first} ${last}`.trim() || 'Guest';   // admin picker
        greetingName = first || displayName;                    // guest greeting
      } else {
        displayName  = resolveGuestGreeting(members, partyNameTrim, primary) || 'Guest';
        greetingName = displayName;                             // multi: surfaces match
      }

      // firstName drives the EnvelopeCanvas big script line. For solo this is
      // the first name only ("Evelyn"). For multi it equals greetingName so
      // the envelope, picker, message body, and public greeting all agree.
      const firstName = isSolo
        ? (members[0].first_name || 'Guest')
        : greetingName;

      results.push({
        key: `party-${p._id || p.id}`,
        type: isSolo ? 'guest' : 'party',
        name: displayName,
        greetingName,
        firstName,
        token,
        guestIds: members.map(g => g.id),
        memberNames: isSolo ? '' : members.map(g => `${g.first_name} ${g.last_name}`).join(', '),
        // Sort keys (Feb 2026) — primary-driven, mirrors greeting precedence.
        sortFirst: (primary.first_name || '').toLowerCase(),
        sortLast:  (primary.last_name  || '').toLowerCase(),
        sortGroup: (primary.group_name || partyNameTrim || displayName || 'zzz').toLowerCase(),
        sortRsvp:  (primary.rsvp_status || 'zzz').toLowerCase(),
      });
    });

    guests.forEach(g => {
      if (guestsWithParty.has(g.id)) return;
      const token = g.party_rsvp_token || g.rsvp_token || '';
      results.push({
        key: `guest-${g.id}`,
        type: 'guest',
        name: `${g.first_name || ''} ${g.last_name || ''}`.trim() || 'Unknown',
        firstName: g.first_name || 'Guest',
        token,
        guestIds: [g.id],
        memberNames: '',
        sortFirst: (g.first_name || '').toLowerCase(),
        sortLast:  (g.last_name  || '').toLowerCase(),
        sortGroup: (g.group_name || g.party_name || `${g.last_name || ''}` || 'zzz').toLowerCase(),
        sortRsvp:  (g.rsvp_status || 'zzz').toLowerCase(),
      });
    });

    return results;
  }, [guests, parties]);

  // Group is "fully invited" only when EVERY member already has an Official
  // Invitation completion. Used by the "Not yet sent" filter.
  const isFullyInvited = (t) =>
    t.guestIds.length > 0 && t.guestIds.every(id => officialCompletions.has(id));

  const filteredTargets = (() => {
    const base = targets()
      .filter(t =>
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.memberNames.toLowerCase().includes(search.toLowerCase())
      )
      .filter(t => filterUnsent ? !isFullyInvited(t) : true);
    if (sortMode === 'default') return base;
    return [...base].sort((a, b) => {
      if (sortMode === 'first') return (a.sortFirst || '').localeCompare(b.sortFirst || '');
      if (sortMode === 'last')  return (a.sortLast  || '').localeCompare(b.sortLast  || '');
      if (sortMode === 'group') return (a.sortGroup || 'zzz').localeCompare(b.sortGroup || 'zzz');
      if (sortMode === 'rsvp')  return (a.sortRsvp  || 'zzz').localeCompare(b.sortRsvp  || 'zzz');
      return 0;
    });
  })();

  // Global "still owed an invitation" count — independent of search so the
  // chip shows absolute remaining work. Equals the number of targets that
  // would remain visible if the "Not yet sent" filter were turned ON.
  const unsentCount = officialStepId
    ? targets().filter(t => !isFullyInvited(t)).length
    : null;

  // A target is selected if any of its guestIds is in selectedGuestIds.
  const isTargetSelected = (t) => t.guestIds.some(id => selectedGuestIds.has(id));

  const toggleSelect = (target) => {
    setGenerated(false);
    setSelectedGuestIds(prev => {
      const next = new Set(prev);
      const currentlySelected = target.guestIds.some(id => next.has(id));
      if (currentlySelected) {
        target.guestIds.forEach(id => next.delete(id));
      } else {
        target.guestIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const selectAll = () => {
    setGenerated(false);
    const allIds = new Set();
    filteredTargets.forEach(t => t.guestIds.forEach(id => allIds.add(id)));
    setSelectedGuestIds(allIds);
  };

  const deselectAll = () => {
    setGenerated(false);
    setSelectedGuestIds(new Set());
  };

  const selectedTargets = targets().filter(isTargetSelected);
  const selectedCount = selectedTargets.length;

  const handleGenerate = () => {
    if (selectedCount === 0) return;
    setGenerated(true);
    // Initialize journey member selection: all members of all selected
    // targets are checked by default. Admin may deselect individuals in the
    // Mark Journey Step card before pressing Mark.
    const allMemberIds = new Set();
    selectedTargets.forEach(t => t.guestIds.forEach(id => allMemberIds.add(id)));
    setJourneyMemberIds(allMemberIds);
  };

  // Per-member toggle inside the Mark Journey Step card.
  const toggleJourneyMember = (gid) => {
    setJourneyMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  };

  // Select-all / Clear-all helper across all listed members.
  const toggleAllJourneyMembers = () => {
    const allIds = selectedTargets.flatMap(t => t.guestIds);
    if (allIds.every(id => journeyMemberIds.has(id))) {
      setJourneyMemberIds(new Set());
    } else {
      setJourneyMemberIds(new Set(allIds));
    }
  };

  // Journey mark — only marks the guests still checked in journeyMemberIds.
  const markJourneyStep = async () => {
    if (!selectedStepId || journeyMemberIds.size === 0) return;
    setMarkingDone(true);
    const allGuestIds = Array.from(journeyMemberIds);
    try {
      // 1) Write the journey-completion (existing behavior)
      await fetch(`${API}/api/journey/steps/${selectedStepId}/completions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_ids: allGuestIds,
          method: 'manual',
          completed_by: 'admin',
          notes: 'Marked via Compose tab — invitation sent',
        }),
      });

      // 2) B1 (Feb 2026): If the marked step is the Official Invitation
      // milestone, ALSO update each affected guest's invitation_status to
      // 'sent'. Without this, the Guest tab "Invite Not Sent" KPI (which
      // counts guests where invitation_status === 'pending') stays stale
      // even though the journey ring lights up. Two surfaces, same intent.
      // Additive only — preserves all existing data and only fires for the
      // specific guests being marked here.
      // Match rule: step_code === 'official_invitation' OR step name
      // equals exactly "Official Invitation" (trimmed, case-insensitive).
      // We deliberately do NOT match "Official Invitation Confirmation"
      // or any other step that happens to contain those words.
      const selectedStep = journeySteps.find(s => (s.id || s._id) === selectedStepId);
      const stepCode = (selectedStep?.step_code || '').trim().toLowerCase();
      const stepName = (selectedStep?.name || selectedStep?.step_name || '').trim().toLowerCase();
      const isOfficialInvitation = stepCode === 'official_invitation' || stepName === 'official invitation';
      if (isOfficialInvitation) {
        await Promise.all(allGuestIds.map(gid =>
          fetch(`${API}/api/guests/${gid}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ invitation_status: 'sent' }),
          }).catch(err => console.error(`Failed to update invitation_status for ${gid}`, err))
        ));
      }

      const newMarked = new Set(markedIds);
      allGuestIds.forEach(id => newMarked.add(id));
      setMarkedIds(newMarked);

      // Refresh Official-Invitation completions cache so the
      // "Not yet sent" filter immediately reflects the new state.
      if (isOfficialInvitation && officialStepId) {
        fetchOfficialCompletions(officialStepId);
      }
    } catch (e) {
      console.error('Journey mark failed', e);
    }
    setMarkingDone(false);
  };

  // ─── Phase 6B — Milo prefill consumer (one-shot, additive only) ─────────
  // Reads `milo_proposal_prefill` from sessionStorage on mount (and again
  // once `partiesLoaded` flips), validates the payload shape, applies the
  // picker filters that ALREADY exist (search · sortMode · filterUnsent),
  // clears the key, and surfaces a gold "Milo prepared this" banner.
  // Does NOT touch the template body. Does NOT pre-select guest IDs.
  // Does NOT submit, save, or send anything. Stale / invalid payloads
  // become a "no longer valid" banner instead of an applied prefill.
  const [miloPrefillBanner, setMiloPrefillBanner] = useState(null);
  // shape: { ok: bool, summary, detail, runbook_family_id, runbook_version, applied: {...} }

  useEffect(() => {
    if (!partiesLoaded) return;
    let raw = null;
    try { raw = sessionStorage.getItem('milo_proposal_prefill'); } catch (_) {}
    if (!raw) return;
    try { sessionStorage.removeItem('milo_proposal_prefill'); } catch (_) {}

    let payload = null;
    try { payload = JSON.parse(raw); } catch (_) {}

    const valid = (
      payload &&
      payload.action_id === 'messaging.open_compose_invite_prefilled' &&
      payload.target &&
      payload.target.kind === 'messaging_compose_prefill' &&
      payload.target.prefill &&
      typeof payload.target.prefill === 'object'
    );

    if (!valid) {
      setMiloPrefillBanner({
        ok: false,
        summary: 'Milo prepared a proposal, but it is no longer valid.',
        detail: 'The prefill payload was malformed or did not match the expected shape. No fields were changed.',
      });
      return;
    }

    const pf = payload.target.prefill || {};
    const applied = {};

    if (typeof pf.search === 'string' && pf.search.length > 0) {
      // Validate against current parties list — refuse to apply a
      // search term that matches NOTHING (likely stale party name).
      const term = pf.search.toLowerCase();
      const hits = (parties || []).filter(p => {
        const name = (p.name || p.last_name || p.party_name || '').toLowerCase();
        const members = (p.member_names || p.memberNames || p.members || []).join
          ? (p.member_names || p.memberNames || p.members || []).join(' ').toLowerCase()
          : '';
        return name.includes(term) || members.includes(term);
      });
      if (hits.length > 0) {
        setSearch(pf.search);
        applied.search = pf.search;
      } else {
        // Soft-fail: leave search untouched, keep the rest of the prefill.
        applied.search = null;
      }
    }
    if (pf.sort_mode === 'rsvp' || pf.sort_mode === 'first' ||
        pf.sort_mode === 'last' || pf.sort_mode === 'group' ||
        pf.sort_mode === 'default') {
      setSortMode(pf.sort_mode);
      applied.sort_mode = pf.sort_mode;
    }
    if (typeof pf.filter_unsent === 'boolean') {
      setFilterUnsent(pf.filter_unsent);
      applied.filter_unsent = pf.filter_unsent;
    }

    setMiloPrefillBanner({
      ok: true,
      summary: payload.summary || 'Milo prepared the picker for you.',
      detail: applied.search === null
        ? `Milo couldn't match "${pf.search}" against any current party — the search field was left untouched. Other picker filters were applied.`
        : 'Picker filters were applied. The template body, recipient checkboxes, and the existing Send / Generate / Save controls remain entirely yours.',
      applied,
      runbook_family_id: payload.runbook_family_id,
      runbook_version: payload.runbook_version,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partiesLoaded]);

  const dismissMiloBanner = () => setMiloPrefillBanner(null);
  // ────────────────────────────────────────────────────────────────────────

  const journeyReady    = generated && selectedTargets.length > 0;
  const showJourneyCard = isWide || journeyReady;

  return (
    <>
      {/* Phase 6B — Milo prefill banner (gold, dismissable, no controls
          beyond Dismiss). Stays read-only — Milo never has a Save / Send
          button on this banner. The operator's existing controls below
          remain the only way anything is sent or persisted. */}
      {miloPrefillBanner && (
        <div data-testid="milo-prefill-banner"
          style={{
            margin: '12px auto 0', maxWidth: 1600,
            padding: '11px 16px', borderRadius: 11,
            border: miloPrefillBanner.ok
              ? '1px solid rgba(198,167,90,0.32)'
              : '1px solid rgba(178,58,72,0.26)',
            background: miloPrefillBanner.ok
              ? 'rgba(198,167,90,0.06)'
              : 'rgba(178,58,72,0.05)',
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
          <span style={{ fontSize: 9, fontWeight: 800,
            letterSpacing: '0.10em', textTransform: 'uppercase',
            padding: '3px 8px', borderRadius: 99,
            background: miloPrefillBanner.ok
              ? 'rgba(198,167,90,0.14)'
              : 'rgba(178,58,72,0.10)',
            color: miloPrefillBanner.ok ? '#8E7A2C' : '#B23A48',
            border: miloPrefillBanner.ok
              ? '1px solid rgba(198,167,90,0.32)'
              : '1px solid rgba(178,58,72,0.26)',
            flexShrink: 0, marginTop: 1 }}>
            {miloPrefillBanner.ok ? 'Milo prepared this' : 'Proposal invalid'}
          </span>
          <div style={{ flex: 1, minWidth: 0, fontSize: 12,
            color: '#1B1812', lineHeight: 1.55 }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>
              {miloPrefillBanner.summary}
            </div>
            <div style={{ color: '#5C5C66' }}>
              {miloPrefillBanner.detail}
              {' '}
              <strong style={{ color: '#1B1812' }}>You are still in
              full control.</strong>
              {' Milo will not send anything — use the existing Generate / Save / Send buttons below to proceed, or edit any field before doing so.'}
            </div>
          </div>
          <button data-testid="milo-prefill-banner-dismiss"
            onClick={dismissMiloBanner}
            style={{ flexShrink: 0, padding: '5px 10px', borderRadius: 7,
              border: '1px solid rgba(0,0,0,0.10)',
              background: '#FFFFFF', color: '#5C5C66',
              fontSize: 10, fontWeight: 700,
              letterSpacing: '0.5px', textTransform: 'uppercase',
              cursor: 'pointer' }}>
            Dismiss
          </button>
        </div>
      )}
    <div style={isWide
      ? { display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr', gap: 20, padding: '20px 24px', alignItems: 'start', maxWidth: 1600, margin: '0 auto' }
      : isMid
      ? { display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 20, padding: '20px 24px', alignItems: 'start', maxWidth: 1280, margin: '0 auto' }
      : { maxWidth: 720, margin: '0 auto', padding: '20px 0' }}>

      {/* Col 1 — Template */}
      <div style={isWide ? { position: 'sticky', top: 16 } : {}}>

      {/* 1. Template editor */}
      <Card title="Invite Message Template" collapsible defaultOpen={isWide}>
        <p style={{ fontSize: 12, color: MUTED, marginBottom: 12, lineHeight: 1.6 }}>
          Edit your template below. Use <code style={{ background: 'rgba(198,167,90,0.12)', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>{'{{name}}'}</code> for the guest name and <code style={{ background: 'rgba(198,167,90,0.12)', padding: '1px 5px', borderRadius: 4, fontSize: 11 }}>{'{{rsvp_link}}'}</code> for their personal invite link.
        </p>
        <textarea
          value={template}
          onChange={e => setTemplate(e.target.value)}
          rows={10}
          data-testid="invite-template-editor"
          style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px',
            borderRadius: 8, border: `1.5px solid ${BORDER}`,
            background: '#FFFEF8', fontFamily: 'monospace', fontSize: 13,
            color: DARK, resize: 'vertical', lineHeight: 1.6, outline: 'none' }}
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <Btn onClick={saveTemplate} disabled={savingTemplate}>
            {savingTemplate ? 'Saving…' : templateSaved ? 'Saved' : 'Save Template'}
          </Btn>
          <Btn variant="ghost" onClick={() => setTemplate(DEFAULT_TEMPLATE)}>
            Reset to Default
          </Btn>
        </div>
      </Card>
      </div>

      {/* Col 2 — Picker + Generated */}
      <div>

      {/* 2. Guest picker — multi-select */}
      <Card title={`Select Guests (${selectedCount} selected)`}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} color={MUTED} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name…"
              data-testid="compose-guest-search"
              style={{ width: '100%', boxSizing: 'border-box', paddingLeft: 32, paddingRight: 12,
                paddingTop: 9, paddingBottom: 9, borderRadius: 8,
                border: `1.5px solid ${BORDER}`, background: '#FFFEF8',
                fontSize: 13, color: DARK, outline: 'none' }}
            />
          </div>
          <Btn variant="ghost" onClick={selectAll} style={{ padding: '7px 12px', fontSize: 11 }}>
            Select All
          </Btn>
          <Btn variant="ghost" onClick={deselectAll} style={{ padding: '7px 12px', fontSize: 11 }}>
            Clear
          </Btn>
        </div>

        {/* Sort + "Not yet sent" filter (Feb 2026) — defaults preserve existing behavior */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: MUTED }}>
              Sort
            </span>
            <select
              value={sortMode}
              onChange={e => setSortMode(e.target.value)}
              data-testid="compose-sort-select"
              style={{ padding: '6px 10px', borderRadius: 8,
                border: `1.5px solid ${BORDER}`, background: '#FFFEF8',
                fontSize: 12, color: DARK, outline: 'none', cursor: 'pointer' }}
            >
              <option value="default">Default</option>
              <option value="first">First</option>
              <option value="last">Last</option>
              <option value="group">Group</option>
              <option value="rsvp">RSVP</option>
            </select>
          </label>

          <label
            data-testid="compose-filter-unsent-label"
            title={officialStepId
              ? 'Hides parties only when every member has been marked Official Invitation complete.'
              : 'Configure the Official Invitation journey step first.'}
            style={{ display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, color: officialStepId ? DARK : MUTED,
              cursor: officialStepId ? 'pointer' : 'not-allowed', userSelect: 'none' }}
          >
            <input
              type="checkbox"
              checked={filterUnsent}
              disabled={!officialStepId}
              onChange={e => setFilterUnsent(e.target.checked)}
              data-testid="compose-filter-unsent"
              style={{ width: 15, height: 15, accentColor: GOLD,
                cursor: officialStepId ? 'pointer' : 'not-allowed' }}
            />
            <span>Not yet sent (Official Invitation)</span>
            {unsentCount !== null && (
              <span
                data-testid="compose-filter-unsent-count"
                style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                  background: unsentCount > 0 ? 'rgba(198,167,90,0.18)' : 'rgba(30,115,68,0.12)',
                  color:      unsentCount > 0 ? '#7A5A1F'              : '#1E7344',
                  border: `1px solid ${unsentCount > 0 ? 'rgba(198,167,90,0.35)' : 'rgba(30,115,68,0.25)'}` }}
              >
                {unsentCount}
              </span>
            )}
          </label>
        </div>

        <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {filteredTargets.length === 0 && (
            <p style={{ fontSize: 12, color: MUTED, textAlign: 'center', padding: '12px 0', fontStyle: 'italic' }}>
              No matches found
            </p>
          )}
          {filteredTargets.map(t => {
            const isSelected = isTargetSelected(t);
            const hasToken = !!t.token;
            return (
              <div
                key={t.key}
                onClick={() => toggleSelect(t)}
                data-testid={`compose-target-${t.key}`}
                style={{ display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', borderRadius: 9, cursor: 'pointer',
                  background: isSelected ? 'rgba(198,167,90,0.10)' : SURFACE,
                  border: `1px solid ${isSelected ? GOLD : 'transparent'}`,
                  transition: 'all 0.12s ease' }}
              >
                <div style={{ width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                  border: `2px solid ${isSelected ? GOLD : '#C0B89A'}`,
                  background: isSelected ? GOLD : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isSelected && <Check size={12} color="#1B1812" strokeWidth={3} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: DARK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t.name}
                  </p>
                  {t.memberNames && (
                    <p style={{ margin: 0, fontSize: 11, color: MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {t.memberNames}
                    </p>
                  )}
                </div>
                <div style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 99,
                  background: hasToken ? 'rgba(30,115,68,0.10)' : 'rgba(180,60,60,0.08)',
                  color: hasToken ? '#1E7344' : '#8B2020',
                  border: `1px solid ${hasToken ? 'rgba(30,115,68,0.25)' : 'rgba(180,60,60,0.20)'}`,
                  flexShrink: 0 }}>
                  {hasToken ? 'Ready' : 'No Token'}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
          <Btn onClick={handleGenerate} disabled={selectedCount === 0 || !partiesLoaded}>
            <ImageIcon size={14} />
            {partiesLoaded
              ? `Generate ${selectedCount > 0 ? `(${selectedCount})` : ''}`
              : 'Loading parties…'}
          </Btn>
          {selectedCount > 0 && !generated && partiesLoaded && (
            <span style={{ fontSize: 11, color: MUTED }}>
              {selectedCount} guest{selectedCount !== 1 ? 's' : ''} selected
            </span>
          )}
        </div>
      </Card>

      {/* 3. Generated results */}
      {generated && selectedTargets.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '1px', color: MUTED, marginBottom: 12 }}>
            Generated — {selectedTargets.length} invite{selectedTargets.length !== 1 ? 's' : ''}
          </div>
          {selectedTargets.map(t => (
            <GeneratedCard key={t.key} target={t} template={template} />
          ))}
        </>
      )}
      </div>

      {/* Col 3 — Mark Journey Step (always rendered at ≥1280px; disabled until Generate) */}
      {showJourneyCard && (
      <div style={isWide ? { position: 'sticky', top: 16 } : isMid ? { gridColumn: '1 / -1' } : {}}>
        <Card title="Mark Journey Step" collapsible defaultOpen={isWide}>
          {!journeyReady ? (
            <p style={{ fontSize: 12, color: MUTED, fontStyle: 'italic', margin: 0, lineHeight: 1.6 }}>
              Generate invites first — then mark the corresponding journey step for the selected guest(s).
            </p>
          ) : (
          <>
          <p style={{ fontSize: 12, color: MUTED, marginBottom: 14, lineHeight: 1.6 }}>
            After sending, mark the corresponding journey step as complete for the selected guest(s).
          </p>
          {journeySteps.length === 0 ? (
            <p style={{ fontSize: 12, color: MUTED, fontStyle: 'italic' }}>
              No journey steps found. Create them in the Journey tab first.
            </p>
          ) : (
            <>
              <select
                value={selectedStepId}
                onChange={e => setSelectedStepId(e.target.value)}
                data-testid="journey-step-select"
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, marginBottom: 12,
                  border: `1.5px solid ${BORDER}`, background: '#FFFEF8',
                  fontSize: 13, color: DARK, outline: 'none', cursor: 'pointer' }}
              >
                <option value="">— Select a journey step —</option>
                {journeySteps.map(s => (
                  <option key={s.id || s._id} value={s.id || s._id}>
                    {s.name || s.step_name || `Step ${s.order || ''}`}
                  </option>
                ))}
              </select>

              {/* Per-member selection (group support). All members of all
                  selected targets shown with checkboxes — default all checked.
                  Mark fires only for checked members. (Feb 2026.) */}
              {(() => {
                const totalMemberCount = selectedTargets.reduce((s, t) => s + t.guestIds.length, 0);
                const allChecked = totalMemberCount > 0
                  && selectedTargets.every(t => t.guestIds.every(id => journeyMemberIds.has(id)));
                return (
                  <div style={{ marginBottom: 14 }} data-testid="journey-member-selection">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: MUTED, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                        Mark for {journeyMemberIds.size} of {totalMemberCount}
                      </span>
                      {totalMemberCount > 1 && (
                        <button
                          type="button"
                          onClick={toggleAllJourneyMembers}
                          data-testid="journey-toggle-all"
                          style={{ background: 'transparent', border: 'none', fontSize: 11, color: GOLD,
                            cursor: 'pointer', padding: 0, fontWeight: 600 }}
                        >
                          {allChecked ? 'Clear all' : 'Select all'}
                        </button>
                      )}
                    </div>
                    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, background: '#FFFEF8', overflow: 'hidden' }}>
                      {selectedTargets.map((t, ti) => {
                        const isMulti = t.guestIds.length > 1;
                        return (
                          <div key={t.key} style={ti > 0 ? { borderTop: `1px solid ${BORDER}` } : {}}>
                            {isMulti && (
                              <div style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600,
                                color: MUTED, background: 'rgba(198,167,90,0.06)' }}>
                                {t.name}
                              </div>
                            )}
                            {t.guestIds.map(gid => {
                              const g = guests.find(x => x.id === gid);
                              if (!g) return null;
                              const checked = journeyMemberIds.has(gid);
                              const fullName = `${g.first_name || ''} ${g.last_name || ''}`.trim() || 'Guest';
                              return (
                                <label
                                  key={gid}
                                  data-testid={`journey-member-row-${gid}`}
                                  style={{ display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: DARK,
                                    userSelect: 'none' }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleJourneyMember(gid)}
                                    data-testid={`journey-member-checkbox-${gid}`}
                                    style={{ width: 16, height: 16, accentColor: GOLD, cursor: 'pointer' }}
                                  />
                                  <span>{fullName}</span>
                                </label>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <Btn
                onClick={markJourneyStep}
                disabled={!selectedStepId || markingDone || journeyMemberIds.size === 0}
                data-testid="journey-mark-button"
              >
                {markingDone
                  ? 'Marking…'
                  : `Mark${journeyMemberIds.size > 0 ? ` ${journeyMemberIds.size}` : ''} as Invitation Sent`}
              </Btn>
            </>
          )}
          </>
          )}
        </Card>
      </div>
      )}
    </div>
    </>
  );
}
